import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { selectGameStatusObject } from '../gamestate/gameStateSlice';
import { selectPlayerEntities } from '../player/playerSlice';

// Define brand colors
const ACCENT_TEAL = '#2ec4b6';
const ACCENT_YELLOW = '#ffe066';
const TEXT_COLOR = '#222';

// Animation keyframes
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled components
const WaitingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
  animation: `${fadeInUp} 0.6s ease-out`,
}));

const WaitingIcon = styled(Box)(({ theme }) => ({
  width: 120,
  height: 120,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${ACCENT_TEAL} 0%, ${ACCENT_YELLOW} 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  animation: `${pulse} 2s ease-in-out infinite`,
  boxShadow: '0 8px 32px rgba(46, 196, 182, 0.3)',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -10,
    left: -10,
    right: -10,
    bottom: -10,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${ACCENT_TEAL}20 0%, ${ACCENT_YELLOW}20 100%)`,
    animation: `${float} 3s ease-in-out infinite`,
    zIndex: -1,
  },
}));

const WaitingTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: '2.5rem',
  fontWeight: 700,
  color: TEXT_COLOR,
  marginBottom: theme.spacing(2),
  textShadow: '2px 2px 4px rgba(46, 196, 182, 0.2)',
}));

const WaitingSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: '1.2rem',
  color: '#666',
  marginBottom: theme.spacing(4),
  maxWidth: '500px',
  lineHeight: 1.6,
}));

const WaitingForClue = () => {
  const gameStatus = useSelector(selectGameStatusObject);
  const players = useSelector(selectPlayerEntities);
  
  const playerInControl = gameStatus.playerInControl;
  const currentPlayer = players[playerInControl];
  const playerName = currentPlayer ? currentPlayer.name : 'Another player';

  return (
    <WaitingContainer>
      <WaitingIcon>
        <Typography 
          variant="h2" 
          sx={{ 
            color: '#fff', 
            fontWeight: 700,
            fontSize: '3rem',
            textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          ⏳
        </Typography>
      </WaitingIcon>
      
      <WaitingTitle>
        Waiting for the Next Clue
      </WaitingTitle>
      
      <WaitingSubtitle>
        {playerName} is choosing...
      </WaitingSubtitle>
    </WaitingContainer>
  );
};

export default WaitingForClue; 